import { apiPost } from "@/shared/api";
import type { MailOverride, MailPreviewRead } from "@/features/mail/api";
import { deriveSupplierMailStatusFromRaw } from "./supplierMailStatus";
import type { Order } from "./types";

export const ALLSERVICE_SKUS = new Set([
  "FE802566-GREY",
  "FE802542-GREY",
  "MO806453",
  "MO806456",
  "MO806459",
  "MO806462",
]);

export type AllServiceLdvResult = {
  order_id: number;
  reference: string;
  supplier_order_id: number;
  file_path: string;
  file_name: string;
  row_quantities: Record<string, number>;
};

export type AllServiceBulkError = {
  order_id: string;
  message: string;
};

export type AllServiceLdvBulkResult = {
  requested: number;
  generated: AllServiceLdvResult[];
  errors: AllServiceBulkError[];
};

export type AllServiceMailBulkSentItem = {
  order_id: number;
  reference: string;
  attachment_name: string;
};

export type AllServiceMailBulkResult = {
  requested: number;
  accepted: boolean;
  recipient: string;
  subject: string;
  message_id: string;
  sent_at: string;
  sent: AllServiceMailBulkSentItem[];
  errors: AllServiceBulkError[];
};

export type AllServiceTrackingPendingItem = {
  supplier_order_id: number;
  reference: string;
  tracking: string;
  carrier: string;
  status: string;
  packages: number;
  source_file: string;
  synced_to_order: boolean;
  order_id: number | null;
};

export type AllServicePoleepoPushDetail = {
  reference: string;
  outcome: string;
};

export type AllServiceTrackingDownloadResult = {
  zip_file: string;
  excel_files: string[];
  parsed: number;
  imported: number;
  backfilled_orders: number;
  files: Array<{
    file_name: string;
    parsed: number;
    imported: number;
    backfilled_orders: number;
  }>;
  pending: AllServiceTrackingPendingItem[];
  /** One-click workflow 2026-05-11: download + import + push automatico a
   *  Poleepo. ``poleepo_pushed`` = tracking propagati al marketplace,
   *  ``poleepo_skipped`` = ordini con stato finale o tracking gia' presente,
   *  ``poleepo_errors`` = chiamate Poleepo fallite (loggate server-side). */
  poleepo_pushed: number;
  poleepo_skipped: number;
  poleepo_errors: number;
  poleepo_details: AllServicePoleepoPushDetail[];
  /** Audit 2026-05-12: breakdown aggregato per outcome ({skip_no_local_order:
   *  58, skip_already_present: 8, error: 1, ...}). Permette il toast con
   *  spiegazione granulare invece di "X saltati" opaco. */
  poleepo_breakdown?: Record<string, number>;
};

export type AllServiceSettings = {
  template_path: string;
  template_configured: boolean;
  template_exists: boolean;
  /** Path Thunderbird per invio mail AS via subprocess ``-compose``.
   *  Sostituisce smtp_credentials (rimosso 2026-05-12). */
  thunderbird_path: string;
  thunderbird_configured: boolean;
  thunderbird_exists: boolean;
  portal_credentials: Record<string, boolean>;
};

export type AllServiceMailStatusState = "confirmed_imap" | "pending_imap" | "never_sent";

export type AllServiceMailStatusDetail = {
  state: AllServiceMailStatusState;
  app_sent_at: string | null;
  app_message_id: string | null;
  app_attachment: string | null;
  app_attempts: number;
  imap_confirmed_at: string | null;
  imap_message_id: string;
  imap_attachment: string;
  recipient: string;
};

export type AllServiceMailStatusResult = {
  order_id: number;
  reference: string;
  status: AllServiceMailStatusDetail;
};

export type AllServiceVerifyMailResult = AllServiceMailStatusResult & {
  scan_metrics: {
    candidates: number;
    scanned: number;
    matched: number;
    marked: number;
    scope: string;
    skipped_reason?: string;
    results?: Record<string, { found: boolean }>;
  };
};

/** True se la stringa SKU normalizzata e' nella whitelist AllService. */
export function isAllServiceSku(sku: string): boolean {
  return ALLSERVICE_SKUS.has(normalizeSku(sku));
}

/** True se uno qualsiasi degli SKU passati e' nella whitelist AllService. */
export function skusContainAllService(skus: readonly string[]): boolean {
  return skus.some((sku) => isAllServiceSku(sku));
}

export function orderHasAllServiceSku(order: Pick<Order, "sku" | "allserviceEligible">): boolean {
  // Il backend marca ``allserviceEligible`` considerando anche i cloni (un clone
  // di uno SKU AS). Fallback alla whitelist client-side sugli SKU grezzi per i
  // mock/test senza il flag o ordini non ancora arricchiti.
  // Un false ESPLICITO vince sulla whitelist: il backend lo manda anche per
  // spegnere ogni tasto fornitore quando il pezzo e' in magazzino BS COM.
  if (typeof order.allserviceEligible === "boolean") return order.allserviceEligible;
  return skusContainAllService(order.sku.split(","));
}

export function ordersWithAllServiceSku<T extends Pick<Order, "sku" | "allserviceEligible">>(
  orders: T[],
): T[] {
  return orders.filter((o) => orderHasAllServiceSku(o));
}

export function generateAllServiceLdvBulk(orderIds: string[]): Promise<AllServiceLdvBulkResult> {
  return apiPost<AllServiceLdvBulkResult>("/api/suppliers/allservice/orders/ldv-bulk", {
    order_ids: orderIds,
  });
}

/** ``opts`` assente/senza ``preview`` → ritorno concreto (call-site esistenti
 *  invariati, nessun type-guard richiesto). ``preview: true`` → il backend
 *  ritorna le bozze senza inviare (union esplicita, va discriminata con
 *  ``"preview" in result``). ``mailOverride`` sovrascrive destinatario/oggetto/
 *  corpo prima dell'invio. */
export function sendAllServiceMailBulk(
  orderIds: string[],
  opts?: { preview?: false; mailOverride?: MailOverride },
): Promise<AllServiceMailBulkResult>;
export function sendAllServiceMailBulk(
  orderIds: string[],
  opts: { preview: boolean; mailOverride?: MailOverride },
): Promise<AllServiceMailBulkResult | MailPreviewRead>;
export function sendAllServiceMailBulk(
  orderIds: string[],
  opts?: { preview?: boolean; mailOverride?: MailOverride },
): Promise<AllServiceMailBulkResult | MailPreviewRead> {
  return apiPost<AllServiceMailBulkResult | MailPreviewRead>(
    "/api/suppliers/allservice/orders/send-mail-bulk",
    {
      order_ids: orderIds,
      preview: opts?.preview ?? false,
      mail_override: opts?.mailOverride ?? null,
    },
  );
}

export function downloadAllServiceTracking(): Promise<AllServiceTrackingDownloadResult> {
  return apiPost<AllServiceTrackingDownloadResult>("/api/suppliers/allservice/tracking/download");
}

export function verifyAllServiceMailSent(orderId: string): Promise<AllServiceVerifyMailResult> {
  return apiPost<AllServiceVerifyMailResult>(
    `/api/suppliers/allservice/orders/${encodeURIComponent(orderId)}/verify-mail-sent`,
  );
}

/** Estrae lo stato mail AS dal raw dell'Order (no fetch). Usato dai badge
 *  in lista per evitare N round-trip: l'OrderRead già espone raw[] con
 *  as_mail_app_sent_at, as_mail_imap_confirmed_at, ecc. */
export function deriveMailStatusFromRaw(
  raw: Record<string, unknown> | null | undefined,
): AllServiceMailStatusDetail {
  return deriveSupplierMailStatusFromRaw(raw, {
    prefix: "as",
    recipient: "biancade.ufficio@allservice.tv",
  });
}

function normalizeSku(value: string): string {
  // Audit 2026-05-11 H6: dopo strip del prefisso ``BS``, consuma anche
  // eventuali spazi/separatori (es. ``"BS FE802566-GREY"`` da cella
  // Excel con whitespace residuo). Stessa logica del backend
  // ``modules/suppliers/adapters/allservice.py:normalize_sku``.
  const sku = value.trim().toUpperCase();
  if (!sku.startsWith("BS")) return sku;
  return sku.slice(2).replace(/^[\s_-]+/, "");
}
